import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RECOMMENDED_GUIDED_INTERVIEW_VERSION } from '@volley/crucible/intake/interview-version.js';
import { useIpcQuery } from '../hooks/useIpc';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { persistScaffoldFirst, persistCreatePrompt } from '../components/scaffold-first-storage';
import { RollbackWarningCard, invalidateGameLists } from './Dashboard';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';
import type { CreateContextAttachment, GuidedInterviewVersion } from '@volley/crucible-ipc-types';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { buildPrereqError } from './prereq-error';
import { buildCreateHandoffRouteState } from './create-handoff';
import {
  Settings as SettingsIcon,
  Home,
  Mic,
  ChevronDown,
  Check,
  X,
  Sparkles,
  MessageSquareText,
  Pencil,
  Frame,
  Plus,
  FileText,
  Info,
} from 'lucide-react';
import type { CreateGameKickoffMode } from '../components/CreateKickoffSelector';
import { deriveCreateDescription } from './create-description';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { useAppFrame } from '../AppFrameContext';
import { useClientCapabilities } from '../transport/context';
import { deriveCreateNameFromConcept } from './create-name';
import { ConceptStudio } from '../components/concept-studio/ConceptStudio';
import { InlineToast } from '../components/ui/toast';
import { voiceInputErrorMessage } from '../lib/voice-error';
import {
  ProjectPlacementSelector,
  type ProjectPlacement,
} from '../components/ProjectPlacementSelector';
import {
  CreateKickoffSelector,
  type VisibleCreateKickoffMode,
} from '../components/CreateKickoffSelector';
import {
  localReadinessSummary,
  useLocalOperationalReadiness,
} from '../local-operational-readiness';
import { INCOMPLETE_TOKEN_VERIFY_MESSAGE, isCompleteTokenVerifyResult } from '../token-verify';
import {
  CREATE_CONTEXT_FILE_ACCEPT,
  hasUsableCreateSeed,
  readCreateContextFiles,
} from '../create-context-files';

type Stage = 'form' | 'creating' | 'success' | 'error';

export const NEW_GAME_DRAFT_STORAGE_KEY = 'crucible:new-game-draft:v1:app';

const DRAFT_PERSIST_DEBOUNCE_MS = 150;
const PROMPT_TEXTAREA_MIN_HEIGHT = 26;
const PROMPT_TEXTAREA_MAX_HEIGHT = 160;

/** Only the explicit Just Build escape hatch may auto-dispatch coding work.
 * Guided creation owns its own interview and plan-approval lifecycle, while
 * Design opens only from its explicit workspace CTA. */
export function shouldAutoKickoffCreate(mode: CreateGameKickoffMode): boolean {
  return mode === 'just-build';
}

type SpeechRecognitionAlternativeLike = {
  transcript: string;
};

type SpeechRecognitionResultLike = {
  readonly length: number;
  item(index: number): SpeechRecognitionAlternativeLike;
};

type SpeechRecognitionResultListLike = {
  readonly length: number;
  item(index: number): SpeechRecognitionResultLike;
};

type SpeechRecognitionEventLike = {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
};

type SpeechRecognitionErrorEventLike = {
  readonly error?: string;
  readonly message?: string;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

type SpeechRecognitionWindow = Window &
  typeof globalThis & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };

function speechRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as SpeechRecognitionWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

interface NewGameDraft {
  concept: string;
  guidedInterviewVersion: GuidedInterviewVersion;
  // crucible#3321 — ONE persisted kickoff state (was a raw/collapsed split:
  // a `CreateGameKickoffMode` `interviewDepth` state plus a derived
  // `VisibleCreateKickoffMode` `kickoffMode`). `'just-build'` is never a
  // persisted SELECTION — it only ever reaches `handleCreate` as an explicit
  // one-shot override from the deliberate prosumer button — so the full
  // kickoff-mode value space collapses to the two persistable choices here.
  kickoffMode: VisibleCreateKickoffMode;
  placement: ProjectPlacement;
}

function isProjectPlacement(value: unknown): value is ProjectPlacement {
  return value === 'local' || value === 'cloud';
}

function isVisibleKickoffMode(value: unknown): value is VisibleCreateKickoffMode {
  return value === 'quick' || value === 'advanced';
}

function isGuidedInterviewVersion(value: unknown): value is GuidedInterviewVersion {
  return value === 'v1' || value === 'v2' || value === 'v3';
}

/**
 * Remaps `'advanced'` to `'quick'` when the Design tab isn't available.
 * Generic over the caller's own kickoff-mode subtype so it serves BOTH the
 * persisted draft state (`VisibleCreateKickoffMode` — 'quick' | 'advanced'
 * only) and `handleCreate`'s one-shot `chosenDepth` (the full
 * `CreateGameKickoffMode`, which can also be 'just-build' via an explicit
 * override) without a second near-duplicate function or a cast at either
 * call site.
 */
function normaliseKickoffMode<T extends CreateGameKickoffMode>(
  value: T,
  flags?: { designTab?: boolean },
): T {
  if ((value as CreateGameKickoffMode) === 'advanced' && flags?.designTab === false) {
    return 'quick' as T;
  }
  return value;
}

function readNewGameDraft(): NewGameDraft {
  const fallback: NewGameDraft = {
    concept: '',
    guidedInterviewVersion: RECOMMENDED_GUIDED_INTERVIEW_VERSION,
    kickoffMode: 'quick',
    placement: 'local',
  };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(NEW_GAME_DRAFT_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      concept: typeof parsed.concept === 'string' ? parsed.concept : '',
      guidedInterviewVersion: isGuidedInterviewVersion(parsed.guidedInterviewVersion)
        ? parsed.guidedInterviewVersion
        : RECOMMENDED_GUIDED_INTERVIEW_VERSION,
      // The persisted JSON key stays `interviewDepth` for byte-compat with
      // drafts written before this collapse (crucible#3321) — only the
      // in-memory shape narrows. A stale draft carrying `'just-build'`
      // (never a persisted selection post-collapse) or the retired `'full'`
      // literal (crucible#2637 / #3313) both fail `isVisibleKickoffMode` and
      // fall back to `'quick'` — the same remap `'full'` already got.
      kickoffMode: isVisibleKickoffMode(parsed.interviewDepth)
        ? normaliseKickoffMode(parsed.interviewDepth)
        : 'quick',
      placement: isProjectPlacement(parsed.placement) ? parsed.placement : 'local',
    };
  } catch {
    return fallback;
  }
}

function writeNewGameDraft(draft: NewGameDraft): void {
  if (typeof window === 'undefined') return;
  try {
    if (draft.concept.trim().length === 0) {
      window.localStorage.removeItem(NEW_GAME_DRAFT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      NEW_GAME_DRAFT_STORAGE_KEY,
      JSON.stringify({
        concept: draft.concept,
        guidedInterviewVersion: draft.guidedInterviewVersion,
        // Persisted key name unchanged (see readNewGameDraft) — only the
        // value space narrowed.
        interviewDepth: draft.kickoffMode,
        placement: draft.placement,
        updatedAt: Date.now(),
      }),
    );
  } catch {
    // localStorage can be unavailable or full; keep typing responsive.
  }
}

function clearNewGameDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(NEW_GAME_DRAFT_STORAGE_KEY);
  } catch {
    // localStorage can be unavailable; clearing in-memory state is enough.
  }
}

/**
 * Route state carried by the preview genre chip's "change" action (live-preview
 * scaffold-first): an explicit recast marker plus, when the per-game cache
 * still has it, the game's original create prompt. The marker distinguishes
 * "recast with no recoverable prompt" (→ blank start-over; the stored draft
 * does NOT seed the form) from an ordinary `/new-game` visit (→ draft-seeded
 * form, unchanged).
 */
export type RecastRouteState = { recast: true; prompt?: string };

/**
 * Build the `/new-game` route state for the chip's "change" action. Pure and
 * exported for unit tests (Workspace wires it straight into `navigate`); pairs
 * with {@link readRecastPromptFromState}. A missing / blank cached prompt still
 * recasts — there is just nothing to pre-fill, so the form starts blank rather
 * than restoring an unrelated stored draft.
 */
export function buildRecastRouteState(prompt: string | null): RecastRouteState {
  return typeof prompt === 'string' && prompt.trim().length > 0
    ? { recast: true, prompt }
    : { recast: true };
}

/**
 * Validate an incoming `/new-game` route state for a "recast" pre-fill. The
 * preview genre chip's "change" action (live-preview scaffold-first) navigates
 * here carrying the original create prompt so the user re-describes rather than
 * starting from a blank box. Route state is `unknown` — a crafted nav or a
 * stale history entry could hand us anything — so require the explicit `recast`
 * marker, then extract the trimmed prompt: a non-empty string pre-fills the
 * form; an absent / invalid / blank prompt yields `''` (recast with nothing
 * recoverable → blank start-over). Anything without the marker returns `null`
 * (→ plain draft-seeded New Game). Exported for unit tests; a pure boundary
 * guard (mirrors the chip's `isScaffoldFirstInference`).
 */
export function readRecastPromptFromState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as { recast?: unknown; prompt?: unknown };
  if (candidate.recast !== true) return null;
  return typeof candidate.prompt === 'string' ? candidate.prompt.trim() : '';
}

export function NewGame({
  initialStage,
  initialProgressMessages,
}: {
  /** Storybook/test only — seed a specific stage. */
  initialStage?: Stage;
  /** Storybook/test only — seed progress messages. */
  initialProgressMessages?: string[];
} = {}): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const flags = useFeatureFlags();
  const frame = useAppFrame();
  const capabilities = useClientCapabilities();
  const { data: newGameConfig } = useIpcQuery('config:get', undefined);
  // crucible#2007 (D-77) — consumer tier hides the Design tab (prosumer-only
  // "write it yourself" entry point); default 'prosumer' when unset.
  const isProsumerTier = newGameConfig?.tier !== 'consumer';
  const localDesignAvailable = flags.designTab && capabilities.hasLocalFilesystem && isProsumerTier;
  // #2058 — Integrated interview. When ON, the adaptive soul-intake IS the one interview;
  // the build then needs ZERO build-phase questions → send it best-effort so "depth: quick"
  // (the dual concept) never surfaces. Independent of the explicit "Just build" pick.
  const intake = flags.intake;
  const { data: ghUser } = useIpcQuery('github:get-user', undefined, { staleTime: 5 * 60_000 });
  const [draftSeed] = useState(readNewGameDraft);
  const location = useLocation();
  // Live-preview scaffold-first: the genre chip's "change" action navigates
  // here with a recast marker (+ the original prompt when the per-game cache
  // still has it) in route state. When present it seeds the form, taking
  // precedence over a stored draft — a recast with no recoverable prompt
  // deliberately starts from a BLANK box rather than restoring an unrelated
  // draft. Absent → the plain draft-seeded form, unchanged. Only the
  // first-mount value matters (it seeds `useState`).
  const recastPrompt = readRecastPromptFromState(location.state);
  const initialConcept = recastPrompt ?? draftSeed.concept;
  const cloudRuntimeAuthority = capabilities.projectPlacementAuthority === 'cloud-runtime';
  const cloudPlacementEnabled =
    cloudRuntimeAuthority || !isProsumerTier || (flags.loaded && flags.cloudProjectPlacement);
  const [placement, setPlacement] = useState<ProjectPlacement>(
    cloudRuntimeAuthority || !isProsumerTier
      ? 'cloud'
      : draftSeed.placement === 'cloud' && (!flags.loaded || cloudPlacementEnabled)
        ? 'cloud'
        : 'local',
  );

  // One-shot: once the form is seeded the recast state has done its job. Strip
  // it from the history entry (same-path replace) so a later Back/Forward
  // remount re-seeds from the stored draft (the user's latest edits) instead of
  // resurrecting — and re-persisting — the original prompt over them. Declared
  // BEFORE the newUi bounce below so that when both fire in the same flush the
  // bounce's navigate runs last and wins. Re-runs are no-ops: after the replace
  // the state is gone, so `recastPrompt` is null.
  useEffect(() => {
    if (recastPrompt !== null) {
      void navigate(location.pathname, { replace: true, state: null });
    }
  }, [recastPrompt, navigate, location.pathname]);

  // Hash URLs and bookmarks can land here even when the newUi flag is off.
  // Bounce back to Dashboard once the flag snapshot has loaded.
  useEffect(() => {
    if (flags.loaded && !flags.newUi) {
      void navigate('/dashboard', { replace: true });
    }
  }, [flags.loaded, flags.newUi, navigate]);

  // Shared "have we already handed off to the workspace?" latch. THREE paths
  // can navigate a completed create into the workspace, and exactly one must
  // win: (1) #2058 integrated soul-intake `intake:ready`, (2) #2561 Part B
  // early `create:setup-ready` (seed written; interview can start while
  // clone/install/build finish in the background), and (3) the post-resolve
  // fallback in `handleCreate` (belt-and-braces for a runtime that never pushed
  // an early-ready event). One ref keeps them from double-navigating.
  const handoffNavigatedRef = useRef(false);

  // #2561 Part B — the handoff intent captured at create start, so the early
  // `create:setup-ready` listener (which fires BEFORE `game:create` resolves)
  // can build the same workspace route state the post-resolve path would.
  const createHandoffRef = useRef<{
    prompt: string;
    autoKickoff: boolean;
    mode?: 'design';
  } | null>(null);

  // #2058 — under the integrated interview, `game:create` runs the adaptive soul-intake and
  // AWAITS the answers. The interview is hosted in the WORKSPACE (one continuous chat), so
  // when `intake:ready` fires (the game dir now exists + turn 0 is ready) navigate there
  // — the Workspace mounts IntegratedSoulInterview, the creator answers, and create finishes
  // in the background, continuing to build + asset review in the same workspace conversation.
  useEffect(() => {
    // Cloud creation remains on this setup surface until the authoritative
    // create result has been catalogued. Local can still overlap setup with
    // the integrated interview.
    if (!flags.intake || placement === 'cloud') return;
    const unsub = window.crucible.on('intake:ready', (event: { gameId?: string }) => {
      // Latch: the ready push has no recovery/dedupe, so a re-fire must not yank
      // the user back to the workspace route a second time.
      if (handoffNavigatedRef.current) return;
      if (event?.gameId) {
        handoffNavigatedRef.current = true;
        const handoff = createHandoffRef.current;
        const routeState = handoff
          ? buildCreateHandoffRouteState({
              prompt: handoff.prompt,
              autoKickoff: handoff.autoKickoff,
              mode: handoff.mode,
              deferDesignHandoff: handoff.mode === 'design',
            })
          : undefined;
        void navigate(
          `/workspace/${placement}/${event.gameId}`,
          routeState ? { state: routeState } : undefined,
        );
      }
    });
    return unsub;
  }, [flags.intake, navigate, placement]);

  // #2561 Part B — the bare "Creating…" forced wait is gone: on the DEFAULT
  // (non-integrated) create, navigate the user into the workspace/interview the
  // moment the seed is on disk (`create:setup-ready`), so they work through the
  // input while clone/install/build finish in the background. Gated to
  // `!intake`: when the integrated soul-intake is on, its own
  // `intake:ready` navigation (above) owns the overlap and mounts the
  // interview turn — navigating earlier than that would race the panel.
  useEffect(() => {
    // The early filesystem handoff is Local-only. A Cloud workspace is not
    // authorized until game.create@1 returns its catalogued result.
    //
    // crucible#3041 — under the split create this listener is the CONVERGED
    // early-nav path for BOTH lanes: main relays core's `phase1-ready`
    // through this same channel (the Design lane's ONLY early signal — it
    // writes no interview seed), and the standalone interview's
    // `intake:ready` follows after navigation. So the intake gate only
    // applies when the split is off.
    if ((flags.intake && !flags.createSplit) || placement === 'cloud') return;
    const unsub = window.crucible.on(
      'create:setup-ready',
      (event: { gameId?: string; interviewExpected?: boolean }) => {
        if (handoffNavigatedRef.current) return;
        if (!event?.gameId) return;
        const handoff = createHandoffRef.current;
        handoffNavigatedRef.current = true;
        // Fire the input/interview UI immediately. `scaffoldFirst` isn't known yet
        // (it resolves after create finishes) — the post-resolve path still
        // persists it to the per-game localStorage cache the Workspace reads, so
        // the genre chip is not lost, it just paints once create completes.
        const routeState = handoff
          ? buildCreateHandoffRouteState({
              prompt: handoff.prompt,
              autoKickoff: handoff.autoKickoff,
              // crucible#3041 — on the split path this listener also serves
              // the Design lane. Defer ONLY when main says an interview will
              // actually run (its done/skip handler is what lifts the defer);
              // a defer with no interview strands the workspace outside
              // Design mode (#3516 round 4). The no-interview Design lane
              // enters Design immediately — the unsplit post-resolve
              // fallback's exact semantics (mode, no defer).
              mode: handoff.mode,
              deferDesignHandoff: handoff.mode === 'design' && event.interviewExpected === true,
            })
          : undefined;
        void navigate(
          `/workspace/${placement}/${event.gameId}`,
          routeState ? { state: routeState } : undefined,
        );
      },
    );
    return unsub;
  }, [flags.intake, flags.createSplit, navigate, placement]);

  const [concept, setConcept] = useState(initialConcept);
  const [contextAttachments, setContextAttachments] = useState<CreateContextAttachment[]>([]);
  const [contextError, setContextError] = useState<string | null>(null);
  // crucible#3321 — ONE kickoff-mode state (was a raw `interviewDepth`
  // state plus a `kickoffMode` value derived from it every render).
  const [kickoffMode, setKickoffMode] = useState<VisibleCreateKickoffMode>(() =>
    placement === 'cloud' && draftSeed.kickoffMode === 'advanced' ? 'quick' : draftSeed.kickoffMode,
  );
  const [guidedInterviewVersion, setGuidedInterviewVersion] = useState<GuidedInterviewVersion>(
    draftSeed.guidedInterviewVersion,
  );
  const localReadiness = useLocalOperationalReadiness(
    capabilities.hasLocalFilesystem && !cloudRuntimeAuthority,
  );
  const cloudAccountQuery = useIpcQuery('cloud-account:get-status', undefined, {
    enabled: cloudPlacementEnabled && !cloudRuntimeAuthority,
    staleTime: 30_000,
  });
  const cloudStatus = cloudRuntimeAuthority
    ? ({ state: 'eligible', egressEnforcement: 'legacy-observed' } as const)
    : cloudPlacementEnabled
      ? (cloudAccountQuery.data ?? {
          state: 'unavailable' as const,
          reason: 'runtime-error' as const,
        })
      : ({ state: 'unavailable', reason: 'disabled' } as const);
  const [studioOpen, setStudioOpen] = useState(false);
  const [stage, setStage] = useState<Stage>(initialStage ?? 'form');
  const [progressMessages, setProgressMessages] = useState<string[]>(initialProgressMessages ?? []);
  // Issue #2561 — every raw progress line (user + dev) kept here for the
  // dev/verbose surface (the on-error debug dump + console.debug). The
  // default loading view reads only `progressMessages` (audience === 'user').
  const [verboseMessages, setVerboseMessages] = useState<string[]>(initialProgressMessages ?? []);
  const [errorState, setErrorState] = useState<SerialisedError | null>(null);
  const [rollbackWarning, setRollbackWarning] = useState<{
    message: string;
    recovery?: string;
  } | null>(null);
  const [creatingKickoffMode, setCreatingKickoffMode] = useState<CreateGameKickoffMode | null>(
    null,
  );

  const promptRef = useRef<HTMLTextAreaElement>(null);
  const designSubmitRef = useRef<HTMLButtonElement>(null);
  const contextInputRef = useRef<HTMLInputElement>(null);
  const contextAttachmentsRef = useRef<CreateContextAttachment[]>([]);
  const promptSpeechRef = useRef<SpeechRecognitionLike | null>(null);
  const draftPersistTimerRef = useRef<number | null>(null);
  const createAttemptRef = useRef<symbol | null>(null);
  const latestDraftRef = useRef<NewGameDraft>({
    concept: initialConcept,
    guidedInterviewVersion: draftSeed.guidedInterviewVersion,
    kickoffMode:
      placement === 'cloud' && draftSeed.kickoffMode === 'advanced'
        ? 'quick'
        : draftSeed.kickoffMode,
    placement,
  });

  useEffect(() => {
    // Pending eligibility is not negative evidence; preserve the immutable choice until it settles.
    if (
      flags.loaded &&
      !cloudAccountQuery.isFetching &&
      cloudStatus.state !== 'eligible' &&
      isProsumerTier &&
      placement === 'cloud'
    ) {
      setPlacement('local');
      latestDraftRef.current = { ...latestDraftRef.current, placement: 'local' };
    }
  }, [cloudAccountQuery.isFetching, cloudStatus.state, flags.loaded, isProsumerTier, placement]);

  useEffect(() => {
    if (isProsumerTier || placement === 'cloud') return;
    setPlacement('cloud');
    setKickoffMode((current) => (current === 'advanced' ? 'quick' : current));
    latestDraftRef.current = {
      ...latestDraftRef.current,
      placement: 'cloud',
      kickoffMode:
        latestDraftRef.current.kickoffMode === 'advanced'
          ? 'quick'
          : latestDraftRef.current.kickoffMode,
    };
  }, [isProsumerTier, placement]);

  /**
   * Selects Cloud and refreshes cached account state after an authentication attempt.
   *
   * Args:
   *   None.
   *
   * Returns:
   *   A promise that settles after any required account-status refresh.
   *
   * Raises:
   *   Error: When the account sign-in or status IPC request fails.
   */
  const handleCloudSelect = useCallback(async () => {
    const attemptedSignIn = cloudStatus.state === 'signed-out';
    const status = attemptedSignIn
      ? await window.crucible.invoke('cloud-account:sign-in')
      : cloudStatus;
    if (status.state === 'eligible') {
      setPlacement('cloud');
      setKickoffMode((current) => (current === 'advanced' ? 'quick' : current));
      latestDraftRef.current = {
        ...latestDraftRef.current,
        placement: 'cloud',
        kickoffMode:
          latestDraftRef.current.kickoffMode === 'advanced'
            ? 'quick'
            : latestDraftRef.current.kickoffMode,
      };
    }
    // Every completed sign-in can change eligibility, including negative outcomes.
    if (attemptedSignIn) {
      await cloudAccountQuery.refetch();
    }
  }, [cloudAccountQuery, cloudStatus]);

  useEffect(() => {
    if (!flags.loaded) return;
    const modeFlags = { designTab: localDesignAvailable };
    setKickoffMode((current) => normaliseKickoffMode(current, modeFlags));
    latestDraftRef.current = {
      ...latestDraftRef.current,
      kickoffMode: normaliseKickoffMode(latestDraftRef.current.kickoffMode, modeFlags),
    };
  }, [flags.loaded, localDesignAvailable]);

  const cancelDraftPersistTimer = useCallback(() => {
    if (draftPersistTimerRef.current === null || typeof window === 'undefined') return;
    window.clearTimeout(draftPersistTimerRef.current);
    draftPersistTimerRef.current = null;
  }, []);

  const clearPromptDraft = useCallback(() => {
    cancelDraftPersistTimer();
    latestDraftRef.current = { concept: '', guidedInterviewVersion, kickoffMode, placement };
    clearNewGameDraft();
    setConcept('');
  }, [cancelDraftPersistTimer, guidedInterviewVersion, kickoffMode, placement]);

  const handleConceptChange = useCallback((value: string) => {
    latestDraftRef.current = {
      concept: value,
      guidedInterviewVersion: latestDraftRef.current.guidedInterviewVersion,
      kickoffMode: latestDraftRef.current.kickoffMode,
      placement: latestDraftRef.current.placement,
    };
    setConcept(value);
  }, []);

  const handleContextFiles = useCallback(async (files: readonly File[]) => {
    if (files.length === 0) return;
    setContextError(null);
    try {
      const next = await readCreateContextFiles(contextAttachmentsRef.current, files);
      contextAttachmentsRef.current = next;
      setContextAttachments(next);
    } catch (err) {
      setContextError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const removeContextAttachment = useCallback((index: number) => {
    const next = contextAttachmentsRef.current.filter((_, candidate) => candidate !== index);
    contextAttachmentsRef.current = next;
    setContextAttachments(next);
    setContextError(null);
  }, []);

  const [promptListening, setPromptListening] = useState(false);
  // #2992 — voice-input failure surfaced to the user (mic denied/missing);
  // null = no error toast shown.
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [speechSupported] = useState(() => speechRecognitionCtor() !== null);

  const handlePromptVoice = useCallback(() => {
    if (promptListening) {
      promptSpeechRef.current?.stop();
      setPromptListening(false);
      return;
    }
    setVoiceError(null);
    const Recognition = speechRecognitionCtor();
    if (!Recognition) {
      promptRef.current?.focus();
      return;
    }
    promptSpeechRef.current?.abort();
    const recognition = new Recognition();
    promptSpeechRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      // #2996 Codex round 2 — a superseded instance's queued callbacks must
      // not touch shared state while a newer attempt is live.
      if (promptSpeechRef.current !== recognition) return;
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results.item(i);
        if (result.length === 0) continue;
        transcript += result.item(0).transcript;
      }
      const spoken = transcript.trim();
      if (!spoken) return;
      const existing = latestDraftRef.current.concept.trim();
      handleConceptChange(existing ? `${existing} ${spoken}` : spoken);
    };
    recognition.onerror = (event) => {
      if (promptSpeechRef.current !== recognition) return;
      setPromptListening(false);
      // #2992 — a denied/missing microphone used to fail silently here.
      const message = voiceInputErrorMessage(event.error, 'type your idea instead');
      if (message) {
        setVoiceError(message);
        promptRef.current?.focus();
      }
    };
    recognition.onend = () => {
      if (promptSpeechRef.current !== recognition) return;
      setPromptListening(false);
    };
    try {
      recognition.start();
      setPromptListening(true);
    } catch {
      setPromptListening(false);
      promptRef.current?.focus();
    }
  }, [handleConceptChange, promptListening]);

  const handleDepthSelect = useCallback((value: VisibleCreateKickoffMode) => {
    latestDraftRef.current = {
      concept: latestDraftRef.current.concept,
      guidedInterviewVersion: latestDraftRef.current.guidedInterviewVersion,
      kickoffMode: value,
      placement: latestDraftRef.current.placement,
    };
    setKickoffMode(value);
  }, []);

  const handleGuidedInterviewVersionChange = useCallback((value: GuidedInterviewVersion) => {
    latestDraftRef.current = { ...latestDraftRef.current, guidedInterviewVersion: value };
    setGuidedInterviewVersion(value);
  }, []);

  const handlePlacementChange = useCallback(
    (value: ProjectPlacement) => {
      if (!isProsumerTier && value !== 'cloud') return;
      setPlacement(value);
      if (value === 'cloud' && kickoffMode === 'advanced') {
        handleDepthSelect('quick');
      }
    },
    [handleDepthSelect, isProsumerTier, kickoffMode],
  );

  useEffect(() => {
    latestDraftRef.current = { concept, guidedInterviewVersion, kickoffMode, placement };
    cancelDraftPersistTimer();
    if (typeof window === 'undefined') return;
    draftPersistTimerRef.current = window.setTimeout(() => {
      writeNewGameDraft(latestDraftRef.current);
      draftPersistTimerRef.current = null;
    }, DRAFT_PERSIST_DEBOUNCE_MS);
  }, [cancelDraftPersistTimer, concept, guidedInterviewVersion, kickoffMode, placement]);

  useEffect(() => {
    return () => {
      cancelDraftPersistTimer();
      writeNewGameDraft(latestDraftRef.current);
    };
  }, [cancelDraftPersistTimer]);

  useEffect(
    () => () => {
      promptSpeechRef.current?.abort();
      promptSpeechRef.current = null;
    },
    [],
  );

  useLayoutEffect(() => {
    const prompt = promptRef.current;
    if (!prompt) return;
    prompt.style.height = 'auto';
    const nextHeight = Math.min(prompt.scrollHeight, PROMPT_TEXTAREA_MAX_HEIGHT);
    prompt.style.height = `${Math.max(nextHeight, PROMPT_TEXTAREA_MIN_HEIGHT)}px`;
    prompt.style.overflowY = 'hidden';
  }, [concept]);

  useEffect(() => {
    if (stage !== 'creating') return;
    const unsub = window.crucible.on('game:create:progress', (event) => {
      if (event.message) {
        // Issue #2561 — split by the structured `audience` tag the main
        // process sets (NOT a regex over the text). Only `audience === 'user'`
        // lines reach the default loading view; the engineering `[create]` /
        // `[ingot]` diagnostics (`audience === 'dev'`) go to the verbose
        // channel only (the on-error debug dump + the dev console). An absent
        // tag is treated as user-facing so nothing is hidden by accident.
        const isDev = event.audience === 'dev';
        setVerboseMessages((prev) => [...prev, event.message]);
        if (isDev) {
          // Keep the internals available to engineers without surfacing them.
          console.debug('[create:verbose]', event.message);
        } else {
          setProgressMessages((prev) => [...prev, event.message]);
        }
      }
      if (event.warning)
        setRollbackWarning({
          message: event.warning,
          ...(event.recovery ? { recovery: event.recovery } : {}),
        });
      if (event.error) {
        setErrorState(unflattenIpcError(event.error));
        setStage('error');
      }
    });
    return unsub;
  }, [stage]);

  const handleCreate = useCallback(
    async (depthOverride?: CreateGameKickoffMode, promptOverride?: string) => {
      // Claim the renderer boundary synchronously. React state alone cannot
      // close the same-tick gap between a double-click and a keyboard shortcut.
      if (createAttemptRef.current !== null) return;

      const chosenDepth = depthOverride ?? kickoffMode;
      // `promptOverride` lets the Concept Studio create directly from its
      // composed pitch without round-tripping through the `concept` textarea
      // (which updates async). Falls back to the typed concept otherwise.
      const conceptText = (promptOverride ?? concept).trim();
      const createAttachments = [...contextAttachmentsRef.current];
      const namingText =
        conceptText ||
        createAttachments
          .map((attachment) =>
            attachment.kind === 'text' ? attachment.text.slice(0, 2_000) : attachment.name,
          )
          .join('\n')
          .trim();
      const cardDescriptionText =
        conceptText ||
        (createAttachments.length > 0
          ? `Built from ${createAttachments.map((attachment) => attachment.name).join(', ')}`
          : '');
      // crucible#2978 — the Design tab ('advanced' depth) is a standalone
      // prosumer entry point: write the design doc yourself, no pitch
      // required. Every OTHER depth still needs a concept (that's the
      // consumer describe-and-interview path `armed` gates in the UI).
      // The Design tab still permits an empty pitch. Context-only guided builds
      // derive their name and dashboard description from the selected files.
      if (!conceptText && createAttachments.length === 0 && chosenDepth !== 'advanced') return;

      // crucible#3321 — `kickoffMode` is now the ONE persisted kickoff state
      // (the `oneInterview`-gated fallback to a separate raw `interviewDepth`
      // state is gone — `isOneInterviewEnabled()` had exactly one consumer,
      // this ternary, and nothing else in the tree reads it anymore).
      if (!isProsumerTier && placement !== 'cloud') {
        setErrorState({
          name: 'CrucibleError',
          message: 'Consumer projects must run in Cloud.',
          category: 'usage',
          retryable: false,
          code: 'CRUCIBLE-CONSUMER-CLOUD-REQUIRED',
          recovery: 'Connect to Cloud and try again.',
        });
        setStage('error');
        return;
      }
      if (chosenDepth === 'advanced' && placement === 'cloud') {
        setErrorState({
          name: 'CrucibleError',
          message: 'Design is not available for Cloud projects yet.',
          category: 'usage',
          retryable: false,
          code: 'CRUCIBLE-CLOUD-DESIGN-UNAVAILABLE',
          recovery: 'Choose Guided build or run this project on This Mac.',
        });
        setStage('error');
        return;
      }
      if (chosenDepth === 'advanced' && !localDesignAvailable) {
        setErrorState({
          name: 'CrucibleError',
          message: 'Design is unavailable in this Foundry runtime.',
          category: 'usage',
          retryable: false,
          code: 'CRUCIBLE-DESIGN-UNAVAILABLE',
          recovery: 'Choose Guided build to continue.',
        });
        setStage('error');
        return;
      }

      // Freeze every create input before the first asynchronous prerequisite
      // check. UI changes made while verification/name suggestion is pending
      // cannot mutate the owning attempt's contract.
      const attempt = Object.freeze({
        token: Symbol('new-game-create-attempt'),
        chosenDepth,
        placement,
        conceptText,
        createAttachments,
        namingText,
        cardDescriptionText,
        guidedInterviewVersion,
        creatorTier: isProsumerTier ? ('prosumer' as const) : ('consumer' as const),
      });
      createAttemptRef.current = attempt.token;
      let attemptSucceeded = false;

      setStage('creating');
      setCreatingKickoffMode(attempt.chosenDepth);
      setProgressMessages([]);
      setVerboseMessages([]);
      setErrorState(null);
      setRollbackWarning(null);
      // #2561 Part B — reset the handoff latch for this create attempt so a
      // retry after an error can navigate again.
      handoffNavigatedRef.current = false;

      try {
        // Project placement owns its prerequisites. A Cloud project is created
        // by Furnace and must not be blocked on this Mac's GitHub, Anthropic, or
        // package-manager configuration.
        if (attempt.placement === 'local') {
          const verify = await window.crucible.invoke('tokens:verify');
          if (!isCompleteTokenVerifyResult(verify)) {
            throw new Error(INCOMPLETE_TOKEN_VERIFY_MESSAGE);
          }
          const prereqErr = buildPrereqError(verify);
          if (prereqErr) {
            setErrorState(prereqErr);
            setStage('error');
            return;
          }

          const ghMissing = verify.github.missingScopes ?? [];
          if (verify.github.status === 'invalid' && ghMissing.length > 0) {
            setErrorState({
              name: 'CrucibleError',
              message: verify.github.error ?? 'Your GitHub token is missing required scopes.',
              category: 'auth',
              retryable: false,
              code: 'CRUCIBLE-207',
              recovery:
                'Open Settings → Connections → GitHub, paste a token with the missing scopes ticked, then try again.',
            });
            setStage('error');
            return;
          }

          if (verify.github.status !== 'valid' || verify.anthropic.status !== 'valid') {
            const missing = [
              verify.github.status !== 'valid' ? 'GitHub' : null,
              verify.anthropic.status !== 'valid' ? 'Anthropic' : null,
            ].filter((value): value is string => value !== null);
            setErrorState({
              name: 'CrucibleError',
              message: `${missing.join(' and ')} must be connected and verified before building on This Mac.`,
              category: 'auth',
              retryable: false,
              code: 'CRUCIBLE-LOCAL-SETUP-REQUIRED',
              recovery: 'Open Settings → Connections, complete setup, then try again.',
            });
            setStage('error');
            return;
          }
        }

        let displayNameToUse: string | undefined;
        try {
          const suggested = await window.crucible.invoke('game:suggest-name', {
            conceptText: attempt.namingText,
            placement: attempt.placement,
          });
          displayNameToUse = suggested.displayName;
        } catch {
          /* non-fatal */
        }

        // #1824 — derive the on-disk / repo slug FROM the concept the user
        // typed, so the project folder + GitHub repo reflect what the game is
        // (e.g. "a peggle-style ball bouncer" → "peggle-style-ball-bouncer")
        // instead of a random codename. A numeric `-2`/`-3` suffix is added
        // ONLY when the clean name collides with an existing local game id;
        // common case is a clean, suffix-free name. Best-effort: fetch the
        // local game list for the collision check, and fall back to an empty
        // set (letting the core CRUCIBLE-201 pre-flight + recovery backstop a
        // collision) if the list isn't available on this transport.
        let existingGameIds: string[] = [];
        try {
          const games = await window.crucible.invoke('game:list:lite');
          existingGameIds = games.map((g) => g.gameId);
        } catch {
          /* non-fatal — collision check is best-effort; core backstops races */
        }
        const nameToUse = deriveCreateNameFromConcept(attempt.namingText, existingGameIds);

        // #1773 — 'just-build' is a renderer-only kickoff mode: the wire
        // carries the persisted depth ('quick') plus the best-effort flag
        // the create core stamps onto the interview seed.
        // #2058 — under One-Interview the primary path is always the single intelligent interview
        // ('quick'); the "write it yourself" link passes 'advanced' explicitly to reach the Design tab.
        const safeInterviewDepth = normaliseKickoffMode(chosenDepth, {
          designTab: localDesignAvailable,
        });
        const justBuild = safeInterviewDepth === 'just-build';
        const wireInterviewDepth =
          safeInterviewDepth === 'just-build' ? 'quick' : safeInterviewDepth;
        const canonicalKickoffMode = justBuild
          ? ('just-build' as const)
          : wireInterviewDepth === 'advanced'
            ? ('adaptive-design' as const)
            : ('adaptive-build' as const);
        // Guided Interview must stop at editable plan review. Reusing the old
        // create-prompt autofire here launched a parallel `/build-game` chat
        // turn while the adaptive interview was still completing; whichever
        // result arrived last could hide the durable review gate. Just Build
        // is the sole path whose contract intentionally auto-dispatches.
        const autoKickoff = shouldAutoKickoffCreate(safeInterviewDepth);
        // Guided Build runs the selected adaptive interview, then hands off to
        // plan review. Design is the intentional blank-canvas path: any prompt
        // or attachments seed Design directly and no adaptive questions run.
        const buildBestEffort = canonicalKickoffMode !== 'adaptive-design';
        // crucible#3305 follow-up — `skipInteractiveIntake` gates whether the
        // adaptive intake RUNS AT ALL; it is true for Just Build and the
        // blank-canvas Design lane, never the full `buildBestEffort`.
        // Adaptive Build sets `buildBestEffort`
        // (telling the SKILL not to ask its own
        // follow-up questions) precisely BECAUSE the adaptive intake DOES run
        // there — the consumer create screen mounts the real interview
        // surface. Sending `interviewBestEffort` alone used to ALSO skip the
        // intake (crucible#3203's gate keyed on the same field), so every
        // consumer-tier create silently skipped its own interview
        // (the intake gate is always true). `skipInteractiveIntake`
        // is the literal "no interview UI attached at all" signal.
        // #2561 Part B — capture the handoff intent BEFORE invoking create, so
        // the early `create:setup-ready` listener (which fires while this
        // invoke is still in flight) can build the same workspace route state.
        createHandoffRef.current = {
          prompt: attempt.conceptText,
          autoKickoff,
          ...(canonicalKickoffMode === 'adaptive-design' ? { mode: 'design' as const } : {}),
        };
        const result = await window.crucible.invoke('game:create', {
          name: nameToUse,
          // #3142 — `nameToUse` was auto-derived from the concept, so core may
          // disambiguate a slug collision (`-2`/`-3`) rather than hard-fail
          // CRUCIBLE-202. Load-bearing in cloud, where the `game:list:lite`
          // collision set above is stubbed empty and the client can't add the
          // suffix itself.
          nameAutoDerived: true,
          ...(displayNameToUse ? { displayName: displayNameToUse } : {}),
          description: deriveCreateDescription(attempt.cardDescriptionText),
          initialPrompt: attempt.conceptText,
          ...(attempt.createAttachments.length > 0
            ? { contextAttachments: attempt.createAttachments }
            : {}),
          interviewDepth: wireInterviewDepth,
          kickoffMode: canonicalKickoffMode,
          ...(justBuild ? {} : { guidedInterviewVersion: attempt.guidedInterviewVersion }),
          creatorTier: attempt.creatorTier,
          ...(buildBestEffort ? { interviewBestEffort: true } : {}),
          ...(justBuild || canonicalKickoffMode === 'adaptive-design'
            ? { skipInteractiveIntake: true }
            : {}),
          // Ingots is the only build path — the GUI always builds with Ingots (#2446).
          buildWithIngots: true,
          placement: attempt.placement,
        });
        attemptSucceeded = true;

        clearPromptDraft();
        contextAttachmentsRef.current = [];
        setContextAttachments([]);
        invalidateGameLists(queryClient);
        // Live-preview scaffold-first (A2.3b): carry the inferred-genre outcome
        // into the Workspace via route state so the preview can paint the genre
        // chip for this create→preview session. Present ONLY when scaffold-first
        // ran (A2.3a surfaces it on the result); omitted otherwise → plain nav.
        const { scaffoldFirst } = result;
        // Web durability: also cache it per-game in localStorage so the chip
        // survives a refresh / re-nav on the crucible-web SPA (where the desktop's
        // crucible.json read via game:status isn't served over the cloud RPC).
        // Best-effort; an immutable create-time hint, so caching what we already
        // have is the right model. Alongside it, cache the original prompt so the
        // chip's "change" action can pre-fill New Game with it (same model — an
        // immutable create-time value the client already has). This runs
        // unconditionally: even when #2561 Part B already navigated the user into
        // the workspace on the early `create:setup-ready`, the Workspace reads the
        // genre chip from this cache, so it is not lost — it paints once create
        // completes here.
        if (scaffoldFirst) {
          persistScaffoldFirst(result.gameId, scaffoldFirst);
          persistCreatePrompt(result.gameId, attempt.conceptText);
        }
        // #2561 Part B — if the early `create:setup-ready` listener already
        // handed off to the workspace (the common case now — the seed is written
        // before install/build finish), do NOT navigate again. Only navigate
        // here when no early-ready fired: the Advanced/Design-tab depth (which
        // writes no interview seed → no `seed-ready`), or a runtime that doesn't
        // push the early-ready event. In those cases this is the sole handoff, so
        // it keeps the original delayed navigate + full route state (mode/chip).
        if (handoffNavigatedRef.current) {
          return;
        }
        handoffNavigatedRef.current = true;
        setStage('success');
        const routeState = buildCreateHandoffRouteState({
          prompt: attempt.conceptText,
          autoKickoff,
          scaffoldFirst,
          mode: wireInterviewDepth === 'advanced' ? 'design' : undefined,
        });
        setTimeout(
          () =>
            navigate(
              `/workspace/${attempt.placement}/${result.gameId}`,
              routeState ? { state: routeState } : undefined,
            ),
          1200,
        );
      } catch (err) {
        setErrorState(unflattenIpcError(err));
        setStage('error');
      } finally {
        // A successful create keeps the claim: navigation owns the rest of
        // this screen's lifetime. Failed owners release only their own token,
        // making one explicit retry available without opening a race window.
        if (!attemptSucceeded && createAttemptRef.current === attempt.token) {
          createAttemptRef.current = null;
        }
      }
    },
    [
      clearPromptDraft,
      concept,
      guidedInterviewVersion,
      kickoffMode,
      intake,
      isProsumerTier,
      navigate,
      placement,
      queryClient,
      localDesignAvailable,
    ],
  );

  const armed = hasUsableCreateSeed(concept, contextAttachments);
  const designDisabledReason =
    placement === 'cloud'
      ? 'Design is not supported for Cloud projects yet. Choose Guided interview or This Mac.'
      : !localDesignAvailable
        ? 'Design is unavailable in this Foundry runtime.'
        : undefined;
  const placementReady =
    placement === 'cloud' ? cloudStatus.state === 'eligible' : localReadiness.creationReady;
  const submitDisabled = !armed || !placementReady;
  const kickoffSubmitDisabled = !placementReady || (kickoffMode !== 'advanced' && !armed);
  const designActionDisabledReason =
    kickoffMode === 'advanced' &&
    placement === 'local' &&
    designDisabledReason === undefined &&
    !localReadiness.creationReady
      ? `Complete This Mac setup before opening Design. ${localReadinessSummary(localReadiness)}`
      : undefined;
  const openLocalSetup = useCallback(() => {
    const destination = localReadiness.issues[0]?.destination ?? 'connections';
    void navigate(`/settings?tab=${destination}`);
  }, [localReadiness.issues, navigate]);
  const handleConceptStudioSubmit = ({
    prompt,
    mode,
  }: {
    prompt: string;
    mode: CreateGameKickoffMode;
  }): void => {
    setStudioOpen(false);
    if (mode === 'advanced') {
      handleConceptChange(prompt);
      handleDepthSelect('advanced');
      window.requestAnimationFrame(() => {
        designSubmitRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        designSubmitRef.current?.focus();
      });
      return;
    }
    void handleCreate(mode, prompt);
  };
  // #2992 — voice-input failure toast (mic permission denied / no device),
  // shared by both the north-star and default form surfaces. Error tone is
  // sticky (role="alert") with an explicit dismiss, per the InlineToast
  // conventions.
  const voiceErrorToast = voiceError ? (
    <InlineToast
      tone="error"
      data-testid="new-game-voice-error"
      onDismiss={() => setVoiceError(null)}
    >
      {voiceError}
    </InlineToast>
  ) : null;
  const contextPickerInput = (
    <input
      ref={contextInputRef}
      type="file"
      multiple
      accept={CREATE_CONTEXT_FILE_ACCEPT}
      className="hidden"
      data-testid="new-game-context-input"
      onChange={(event) => {
        const files = Array.from(event.currentTarget.files ?? []);
        event.currentTarget.value = '';
        void handleContextFiles(files);
      }}
    />
  );
  const contextTray =
    contextAttachments.length > 0 || contextError ? (
      <div
        className="mt-3 rounded-2xl border border-white/[0.09] bg-white/[0.025] p-3"
        data-testid="new-game-context-tray"
      >
        <p className="mb-2 text-xs font-medium text-white/65">Build context</p>
        {contextAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {contextAttachments.map((attachment, index) => (
              <div
                key={`${attachment.kind}:${attachment.name}:${index}`}
                className="group relative flex h-14 max-w-[180px] items-center gap-2 overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] pr-7 text-xs text-white/65"
                data-testid="new-game-context-chip"
              >
                {attachment.kind === 'image' ? (
                  <img
                    src={`data:${attachment.mimeType};base64,${attachment.base64}`}
                    alt={attachment.name}
                    className="h-full w-16 shrink-0 object-cover"
                  />
                ) : (
                  <span className="flex h-full w-10 shrink-0 items-center justify-center bg-white/[0.04] text-[10px] font-medium uppercase text-white/40">
                    {attachment.name.split('.').pop()?.slice(0, 4) ?? 'doc'}
                  </span>
                )}
                <span className="truncate" title={attachment.name}>
                  {attachment.name}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${attachment.name}`}
                  onClick={() => removeContextAttachment(index)}
                  className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white/45 hover:bg-white/10 hover:text-white"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        {contextError && (
          <p className="mt-2 text-xs text-red-300" role="alert">
            {contextError}
          </p>
        )}
        <p className="mt-2 text-[11px] text-white/35">
          Reference files are copied into this project. PNG, JPEG, WebP, Markdown, text, CSV, JSON,
          and YAML are supported.
        </p>
      </div>
    ) : null;

  if (flags.loaded && flags.intake && flags.newUi) {
    // Issue #2561 — friendly default headline; only user-facing lines feed it.
    const latestProgress =
      progressMessages.length > 0
        ? progressMessages[progressMessages.length - 1]
        : 'Setting up your game…';
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <FoundryShellHeader
          chrome={frame === 'embedded' ? 'web' : 'electron'}
          showBrandMark={frame === 'fullscreen'}
          className={frame === 'embedded' ? 'border-b-0 bg-transparent' : undefined}
          // Build/Design/Art are the core tabs everywhere in Foundry — this
          // screen (pre-game-creation) shares one selection state with the
          // lower kickoff selector. These tabs select a path; only the lower
          // CTA crosses the explicit game-creation boundary.
          // Art has nothing to show before a game exists, so it's visible
          // but inert (matches the honest "coming soon" treatment elsewhere).
          // crucible#2007 (D-77) — consumer gets no tab bar at all here: "the
          // point of consumer is one path" (Sean). Omitting `center` falls
          // back to FoundryShellHeader's own `showBrandMark` branch (just the
          // Foundry logo, centered) — no code duplication needed. Switching
          // back to Prosumer requires restarting without the explicit
          // developer-only creator-tier override.
          center={
            isProsumerTier ? (
              <div
                className="app-region-no-drag flex h-10 items-center gap-0.5 rounded-full px-1"
                style={{
                  background: 'var(--surface-mode-pill-bar)',
                  boxShadow: 'var(--surface-mode-pill-bar-ring)',
                }}
              >
                <button
                  type="button"
                  className="mode-pill"
                  aria-pressed={kickoffMode === 'quick'}
                  data-active={kickoffMode === 'quick'}
                  data-testid="new-game-build-tab"
                  onClick={() => handleDepthSelect('quick')}
                  title="Build — describe & interview"
                >
                  <MessageSquareText size={14} strokeWidth={1.75} />
                  <span>Build</span>
                </button>
                <button
                  type="button"
                  className="mode-pill ml-3.5"
                  aria-pressed={kickoffMode === 'advanced'}
                  aria-disabled={designDisabledReason !== undefined}
                  aria-describedby={
                    designDisabledReason ? 'new-game-design-unavailable-reason' : undefined
                  }
                  data-active={kickoffMode === 'advanced'}
                  data-disabled={designDisabledReason !== undefined}
                  data-testid="new-game-design-tab"
                  onClick={() => {
                    if (!designDisabledReason) handleDepthSelect('advanced');
                  }}
                  title={
                    designDisabledReason ??
                    'Design — skip the interview and author the design doc yourself'
                  }
                >
                  <Pencil size={14} strokeWidth={1.75} />
                  <span>Design</span>
                </button>
                {designDisabledReason && (
                  <span id="new-game-design-unavailable-reason" className="sr-only">
                    {designDisabledReason}
                  </span>
                )}
                {flags.artTab && flags.newUi && (
                  <button
                    type="button"
                    className="mode-pill ml-3.5"
                    data-active="false"
                    data-disabled="true"
                    data-testid="new-game-art-tab"
                    disabled
                    title="Art — available once your game is building"
                  >
                    <Frame size={14} strokeWidth={1.75} />
                    <span>Art</span>
                  </button>
                )}
              </div>
            ) : undefined
          }
          leading={
            <Link
              to="/dashboard"
              className="app-region-no-drag flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.11] transition-all hover:border-white/[0.18]"
              style={{
                background: 'var(--color-crucible-bg)',
                color: 'var(--text-3)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }}
              aria-label="Back to Dashboard"
            >
              <Home size={16} strokeWidth={1.75} />
            </Link>
          }
          actions={
            <div
              className="app-region-no-drag flex items-center gap-1 rounded-full px-1 py-1"
              style={{ background: 'var(--bg-elev-1)' }}
            >
              <button
                onClick={() => navigate('/settings')}
                className="rounded-full border border-white/[0.11] p-1.5 transition-all hover:border-white/[0.18]"
                style={{
                  background: 'var(--color-crucible-bg)',
                  color: 'var(--text-3)',
                  transitionTimingFunction: 'var(--ease-flora)',
                  transitionDuration: 'var(--motion-fast)',
                }}
                aria-label="Settings"
              >
                <SettingsIcon size={16} strokeWidth={1.75} />
              </button>
              {ghUser && (
                <img
                  src={ghUser.avatarUrl}
                  alt={ghUser.login}
                  title={ghUser.login}
                  className="h-8 w-8 rounded-full object-cover ring-1 ring-white/10"
                />
              )}
            </div>
          }
        />

        {voiceErrorToast}
        {contextPickerInput}

        {/* Sean, 2026-07-16 — PROSUMER dropped the PreviewOverlay TV+phone
            split for this screen: it forced CreateKickoffSelector (built for
            a wide desktop-form layout, unchanged from main) into a hardcoded
            360-380px phone-simulation column (PreviewOverlay.tsx's
            CONTROLLERS_MIN_W/lg:w-[var(--preview-ctrl-w,...)]), which
            overlapped its "Recommended" badge against the label text. The TV
            side never showed anything genuinely differentiated at this step
            either (just a mirrored copy of the phone's own state) — the
            split earns its keep once actual interview turns begin (a
            different page, /workspace/<gameId>, reached via the early-nav
            handoff below), not for the plain describe/kickoff step. Prosumer
            now renders the SAME body content the non-north-star branch below
            does, so this screen matches main's shipped layout exactly.
            CONSUMER (2026-07-16, later the same day) gets the split back —
            "the point of consumer is one path and TV/phone proxies" (Sean) —
            but with a purpose-built minimal phone pane (ConsumerDescribeStage
            below), not CreateKickoffSelector, so the original cramping bug
            doesn't recur. */}
        <main
          className={
            stage === 'form' && !isProsumerTier
              ? 'flex flex-1 overflow-hidden'
              : stage === 'form' && isProsumerTier
                ? 'flex min-h-0 flex-1 overflow-hidden'
                : 'flex flex-1 flex-col items-center justify-center overflow-hidden px-6 pb-8'
          }
        >
          {stage === 'form' &&
            (isProsumerTier ? (
              <div
                className="grid min-h-0 flex-1 grid-cols-1 overflow-auto lg:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)] lg:overflow-hidden xl:grid-cols-[minmax(0,2fr)_minmax(420px,1fr)]"
                data-testid="new-game-workbench"
              >
                <section
                  className="px-8 py-10 lg:overflow-y-auto lg:px-12 xl:px-20 xl:py-16"
                  data-testid="new-game-workbench-brief"
                >
                  <div className="mx-auto w-full max-w-4xl">
                    <h1
                      className="text-[2.25rem] font-light tracking-[-0.025em] xl:text-[2.5rem]"
                      style={{ color: 'var(--text-1)' }}
                    >
                      What would you like to build?
                    </h1>

                    <div
                      className="relative mt-8 rounded-2xl border border-white/[0.14] bg-white/[0.025] focus-within:border-white/[0.24]"
                      data-testid="new-game-concept-field"
                    >
                      <textarea
                        ref={promptRef}
                        value={concept}
                        onChange={(event) => handleConceptChange(event.target.value)}
                        onKeyDown={(event) => {
                          if (
                            event.key === 'Enter' &&
                            (event.metaKey || event.ctrlKey) &&
                            kickoffMode === 'quick' &&
                            !kickoffSubmitDisabled
                          ) {
                            void handleCreate('quick');
                          }
                        }}
                        placeholder="Describe the game you want to build..."
                        autoFocus
                        rows={8}
                        className="min-h-[260px] w-full resize-y bg-transparent px-6 pb-16 pt-5 text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                        style={{ color: 'var(--text-1)' }}
                      />
                      <div className="absolute bottom-3 right-3 flex items-center gap-1">
                        {concept.length > 0 && (
                          <button
                            type="button"
                            title="Clear prompt"
                            aria-label="Clear prompt"
                            onClick={clearPromptDraft}
                            className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                            style={{ color: 'var(--text-3)' }}
                          >
                            <X size={15} strokeWidth={1.9} />
                          </button>
                        )}
                        <button
                          type="button"
                          title={speechSupported ? 'Voice idea' : 'Type idea'}
                          aria-label="Voice idea"
                          aria-pressed={promptListening}
                          data-testid="new-game-voice-button"
                          onClick={handlePromptVoice}
                          className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                            promptListening
                              ? 'bg-crucible-accent text-black'
                              : 'hover:bg-white/[0.08]'
                          }`}
                          style={{ color: promptListening ? undefined : 'var(--text-3)' }}
                        >
                          <Mic size={16} strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>

                    <div className="mt-8" data-testid="new-game-context-gallery">
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-medium text-white/75">Context</h2>
                        <span
                          title="Add screenshots, design documents, or notes that explain your vision."
                          className="text-white/35"
                        >
                          <Info size={14} strokeWidth={1.8} aria-hidden="true" />
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                        {contextAttachments.map((attachment, index) => (
                          <article
                            key={`${attachment.kind}:${attachment.name}:${index}`}
                            className="group relative flex min-h-36 overflow-hidden rounded-xl border border-white/[0.12] bg-white/[0.025]"
                            data-testid="new-game-context-card"
                          >
                            {attachment.kind === 'image' ? (
                              <img
                                src={`data:${attachment.mimeType};base64,${attachment.base64}`}
                                alt={attachment.name}
                                className="h-36 w-full object-cover"
                              />
                            ) : (
                              <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-5 py-4">
                                <FileText size={20} strokeWidth={1.6} className="text-white/55" />
                                <span
                                  className="truncate text-sm text-white/75"
                                  title={attachment.name}
                                >
                                  {attachment.name}
                                </span>
                                <span className="text-xs text-white/35">
                                  {attachment.mimeType === 'text/markdown'
                                    ? 'Markdown'
                                    : 'Document'}{' '}
                                  · {Math.max(1, Math.round(attachment.byteLength / 1024))} KB
                                </span>
                              </div>
                            )}
                            <button
                              type="button"
                              aria-label={`Remove ${attachment.name}`}
                              onClick={() => removeContextAttachment(index)}
                              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-black/45 text-white/65 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"
                            >
                              <X size={13} />
                            </button>
                          </article>
                        ))}

                        <button
                          type="button"
                          title="Add screenshots or documents"
                          aria-label="Add context"
                          onClick={() => contextInputRef.current?.click()}
                          className="flex min-h-36 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-white/[0.18] bg-white/[0.015] text-white/55 transition-colors hover:border-white/[0.28] hover:bg-white/[0.035] hover:text-white/75"
                          data-testid="new-game-add-context-card"
                        >
                          <Plus size={20} strokeWidth={1.6} />
                          <span className="text-sm">Add context</span>
                          <span className="text-xs text-white/35">Images or documents</span>
                        </button>
                      </div>

                      {contextError && (
                        <p className="mt-3 text-xs text-red-300" role="alert">
                          {contextError}
                        </p>
                      )}
                      <p className="mt-3 text-xs leading-relaxed text-white/35">
                        Add screenshots, docs, or notes to give Foundry more context about your
                        vision.
                      </p>
                    </div>

                    {!localReadiness.falConfigured && placement === 'local' && (
                      <p className="mt-4 text-xs leading-relaxed text-white/45">
                        Fal.ai is optional. Foundry will use fallback SVG artwork until it is
                        connected.
                      </p>
                    )}

                    {flags.foundryPrototypes && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setStudioOpen(true)}
                          data-testid="new-game-concept-studio"
                          className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] px-4 py-2 text-[13px] transition-colors hover:bg-white/[0.06]"
                          style={{ color: 'var(--text-2)' }}
                        >
                          <Sparkles size={14} className="text-crucible-accent-hover" />
                          Help me shape an idea
                        </button>
                      </div>
                    )}
                  </div>
                </section>

                <aside
                  className="flex min-h-[520px] flex-col border-t border-white/[0.1] p-8 lg:min-h-0 lg:overflow-y-auto lg:border-l lg:border-t-0 xl:p-10"
                  data-testid="new-game-workbench-options"
                >
                  {!cloudRuntimeAuthority && (
                    <>
                      <ProjectPlacementSelector
                        value={placement}
                        onChange={handlePlacementChange}
                        cloudStatus={cloudStatus}
                        onCloudSelect={handleCloudSelect}
                        localSelectable={localReadiness.creationReady}
                        localReason={localReadinessSummary(localReadiness)}
                        onOpenLocalSetup={openLocalSetup}
                        presentation="workbench"
                      />
                      <div className="my-8 border-t border-white/[0.1]" />
                    </>
                  )}

                  <div className="min-h-0 flex-1">
                    <CreateKickoffSelector
                      mode={kickoffMode}
                      onModeChange={handleDepthSelect}
                      guidedInterviewVersion={guidedInterviewVersion}
                      onGuidedInterviewVersionChange={handleGuidedInterviewVersionChange}
                      onSubmit={() => void handleCreate(kickoffMode)}
                      submitDisabled={kickoffSubmitDisabled}
                      seedReady={armed}
                      designDisabledReason={designDisabledReason}
                      actionDisabledReason={designActionDisabledReason}
                      onActionDisabledHelp={designActionDisabledReason ? openLocalSetup : undefined}
                      submitButtonRef={designSubmitRef}
                      presentation="workbench"
                      secondaryAction={
                        placement === 'local' && kickoffMode === 'quick' ? (
                          <button
                            type="button"
                            onClick={() => void handleCreate('just-build')}
                            disabled={submitDisabled}
                            data-testid="new-game-just-build"
                            className="mt-3 w-full text-center text-xs text-white/40 transition-colors hover:text-white/65 disabled:cursor-not-allowed disabled:text-white/15"
                          >
                            Skip interview — just build
                          </button>
                        ) : undefined
                      }
                    />
                  </div>
                </aside>
              </div>
            ) : (
              <ConsumerDescribeStage
                concept={concept}
                onConceptChange={handleConceptChange}
                onSubmit={() => void handleCreate('quick')}
                promptRef={promptRef}
                speechSupported={speechSupported}
                promptListening={promptListening}
                onVoiceClick={handlePromptVoice}
                submitDisabled={submitDisabled}
                placementBlocked={!placementReady && placement === 'local'}
                localReadinessReason={localReadinessSummary(localReadiness)}
                onOpenLocalSetup={openLocalSetup}
                onOpenStudio={() => setStudioOpen(true)}
              />
            ))}

          {stage === 'creating' && (
            <div className="flex flex-col items-center gap-5" aria-busy="true">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/[0.12] border-t-white/50" />
              <p
                className="text-center text-[15px] transition-all duration-300"
                style={{ color: 'var(--text-2)' }}
                role="status"
                aria-live="polite"
              >
                {creatingKickoffMode === 'advanced'
                  ? 'Creating your Design workspace…'
                  : latestProgress}
              </p>
              {creatingKickoffMode === 'advanced' && progressMessages.length > 0 && (
                <p className="text-center text-[13px]" style={{ color: 'var(--text-3)' }}>
                  {latestProgress}
                </p>
              )}
              {progressMessages.length > 1 && (
                <div className="flex flex-col items-center gap-1">
                  {progressMessages.slice(0, -1).map((msg, i) => (
                    <p
                      key={i}
                      className="text-center text-[13px]"
                      style={{ color: 'var(--text-3)' }}
                    >
                      {msg}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {stage === 'success' && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#22c55e"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold">Game Created!</h2>
              <p className="text-sm text-white/50">Opening workspace...</p>
            </div>
          )}

          {stage === 'error' && errorState && (
            <div className="flex w-full max-w-2xl flex-col gap-4">
              <h2 className="text-lg font-semibold">Creation Failed</h2>
              <CrucibleErrorState
                error={errorState}
                onRetry={() => {
                  setStage('form');
                  setProgressMessages([]);
                  setVerboseMessages([]);
                  setErrorState(null);
                  setRollbackWarning(null);
                }}
              />
              {rollbackWarning && (
                <RollbackWarningCard
                  message={rollbackWarning.message}
                  recovery={rollbackWarning.recovery}
                />
              )}
              {/* Issue #2561 — on failure, show the FULL raw stream (user + dev
                  lines) so engineers can debug; dev/verbose surface, gated
                  behind the error state. */}
              {verboseMessages.length > 0 && (
                <div className="max-h-32 overflow-auto rounded-lg bg-black/30 p-3">
                  {verboseMessages.map((msg, i) => (
                    <p key={i} className="text-xs text-white/50">
                      {msg}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}
        </main>
        <ConceptStudio
          open={studioOpen}
          onClose={() => setStudioOpen(false)}
          designDisabledReason={designDisabledReason}
          onSubmit={handleConceptStudioSubmit}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <FoundryShellHeader
        chrome={frame === 'embedded' ? 'web' : 'electron'}
        showBrandMark={frame === 'fullscreen'}
        className={frame === 'embedded' ? 'border-b-0 bg-transparent' : undefined}
        leading={
          <Link
            to="/dashboard"
            className="app-region-no-drag flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.11] transition-all hover:border-white/[0.18]"
            style={{
              background: 'var(--color-crucible-bg)',
              color: 'var(--text-3)',
              transitionTimingFunction: 'var(--ease-flora)',
              transitionDuration: 'var(--motion-fast)',
            }}
            aria-label="Back to Dashboard"
          >
            <Home size={16} strokeWidth={1.75} />
          </Link>
        }
        actions={
          <div
            className="flex items-center gap-1 rounded-full px-1 py-1"
            style={{ background: 'var(--bg-elev-1)' }}
          >
            <button
              onClick={() => navigate('/settings')}
              className="rounded-full border border-white/[0.11] p-1.5 transition-all hover:border-white/[0.18]"
              style={{
                background: 'var(--color-crucible-bg)',
                color: 'var(--text-3)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }}
              aria-label="Settings"
            >
              <SettingsIcon size={16} strokeWidth={1.75} />
            </button>
            {ghUser && (
              <img
                src={ghUser.avatarUrl}
                alt={ghUser.login}
                title={ghUser.login}
                className="h-8 w-8 rounded-full object-cover ring-1 ring-white/10"
              />
            )}
          </div>
        }
      />

      {voiceErrorToast}
      {contextPickerInput}

      <main className="flex flex-1 flex-col items-center justify-center overflow-hidden px-6 pb-8">
        {stage === 'form' && (
          <div className="w-full max-w-3xl">
            {/* Gemini-style heading */}
            <div className="flex flex-col items-center pb-6">
              <h2
                className="text-center"
                style={{
                  color: 'var(--text-1)',
                  fontSize: '2.5rem',
                  fontWeight: 300,
                  letterSpacing: '-0.015em',
                }}
              >
                What would you like to build?
              </h2>
            </div>

            {/* Pill input */}
            <div
              className="flex items-end gap-2 rounded-[28px] px-3 py-3"
              style={{
                background: 'var(--surface-chat)',
                boxShadow: 'var(--surface-chat-shadow)',
              }}
            >
              {/* Plus / add context */}
              <button
                type="button"
                title="Add reference files"
                aria-label="Add context"
                onClick={() => contextInputRef.current?.click()}
                className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                style={{ color: 'var(--text-3)' }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M6.5 1v11M1 6.5h11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {/* Textarea */}
              <textarea
                ref={promptRef}
                value={concept}
                onChange={(e) => handleConceptChange(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    (e.metaKey || e.ctrlKey) &&
                    kickoffMode === 'quick' &&
                    !kickoffSubmitDisabled
                  ) {
                    void handleCreate('quick');
                  }
                }}
                placeholder="Describe the game you want to build..."
                autoFocus
                rows={1}
                className="max-h-40 min-h-[26px] flex-1 resize-none overflow-hidden border-0 bg-transparent text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                style={{ color: 'var(--text-1)' }}
              />

              {concept.length > 0 && (
                <button
                  type="button"
                  title="Clear prompt"
                  aria-label="Clear prompt"
                  onClick={clearPromptDraft}
                  className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                  style={{ color: 'var(--text-3)' }}
                >
                  <X size={15} strokeWidth={1.9} />
                </button>
              )}

              <button
                type="button"
                title={speechSupported ? 'Voice idea' : 'Type idea'}
                aria-label="Voice idea"
                aria-pressed={promptListening}
                data-testid="new-game-voice-button"
                onClick={handlePromptVoice}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                  promptListening ? 'bg-crucible-accent text-black' : 'hover:bg-white/[0.08]'
                }`}
                style={{ color: promptListening ? undefined : 'var(--text-3)' }}
              >
                <Mic size={16} strokeWidth={1.75} />
              </button>
            </div>

            {contextTray}

            {!cloudRuntimeAuthority && isProsumerTier && (
              <ProjectPlacementSelector
                value={placement}
                onChange={handlePlacementChange}
                cloudStatus={cloudStatus}
                onCloudSelect={handleCloudSelect}
                localSelectable={localReadiness.creationReady}
                localReason={localReadinessSummary(localReadiness)}
                onOpenLocalSetup={openLocalSetup}
              />
            )}

            <div className="mt-5">
              <CreateKickoffSelector
                mode={kickoffMode}
                onModeChange={handleDepthSelect}
                guidedInterviewVersion={guidedInterviewVersion}
                onGuidedInterviewVersionChange={handleGuidedInterviewVersionChange}
                onSubmit={() => void handleCreate(kickoffMode)}
                submitDisabled={kickoffSubmitDisabled}
                seedReady={armed}
                designDisabledReason={designDisabledReason}
                actionDisabledReason={designActionDisabledReason}
                onActionDisabledHelp={designActionDisabledReason ? openLocalSetup : undefined}
                submitButtonRef={designSubmitRef}
              />
              {/* #1773 / crucible#3321 — see the sibling mount above for the
                  full rationale: a deliberate, local prosumer-only eval shortcut. */}
              {placement === 'local' && kickoffMode === 'quick' && (
                <button
                  type="button"
                  onClick={() => void handleCreate('just-build')}
                  disabled={submitDisabled}
                  data-testid="new-game-just-build"
                  className="mt-2 text-[11px] text-white/35 underline decoration-white/20 underline-offset-2 transition-colors hover:text-white/55 disabled:cursor-not-allowed disabled:text-white/15 disabled:no-underline"
                >
                  Skip the interview — just build
                </button>
              )}
            </div>

            {!localReadiness.falConfigured && placement === 'local' && (
              <p className="mt-2 text-xs leading-relaxed text-white/45">
                Fal.ai is optional. Foundry will use fallback SVG artwork until it is connected.
              </p>
            )}

            {flags.foundryPrototypes && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={() => setStudioOpen(true)}
                  data-testid="new-game-concept-studio"
                  className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] px-4 py-2 text-[13px] transition-colors hover:bg-white/[0.06]"
                  style={{ color: 'var(--text-2)' }}
                >
                  <Sparkles size={14} className="text-crucible-accent-hover" />
                  Help me shape an idea
                </button>
              </div>
            )}
          </div>
        )}

        {stage === 'creating' && (
          <div className="flex flex-col items-center gap-5" aria-busy="true">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/[0.12] border-t-white/50" />
            <p
              className="text-center text-[15px] transition-all duration-300"
              style={{ color: 'var(--text-2)' }}
              role="status"
              aria-live="polite"
            >
              {creatingKickoffMode === 'advanced'
                ? 'Creating your Design workspace…'
                : progressMessages.length > 0
                  ? progressMessages[progressMessages.length - 1]
                  : 'Setting up your game…'}
            </p>
            {creatingKickoffMode === 'advanced' && progressMessages.length > 0 && (
              <p className="text-center text-[13px]" style={{ color: 'var(--text-3)' }}>
                {progressMessages[progressMessages.length - 1]}
              </p>
            )}
            {progressMessages.length > 1 && (
              <div className="flex flex-col items-center gap-1">
                {progressMessages.slice(0, -1).map((msg, i) => (
                  <p key={i} className="text-center text-[13px]" style={{ color: 'var(--text-3)' }}>
                    {msg}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {stage === 'success' && (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#22c55e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">Game Created!</h2>
            <p className="text-sm text-white/50">Opening workspace...</p>
          </div>
        )}

        {stage === 'error' && errorState && (
          <div className="flex w-full max-w-2xl flex-col gap-4">
            <h2 className="text-lg font-semibold">Creation Failed</h2>
            <CrucibleErrorState
              error={errorState}
              onRetry={() => {
                setStage('form');
                setProgressMessages([]);
                setVerboseMessages([]);
                setErrorState(null);
                setRollbackWarning(null);
              }}
            />
            {rollbackWarning && (
              <RollbackWarningCard
                message={rollbackWarning.message}
                recovery={rollbackWarning.recovery}
              />
            )}
            {/* Issue #2561 — on failure, show the FULL raw stream (user + dev
                lines) so engineers can debug; dev/verbose surface, gated
                behind the error state. */}
            {verboseMessages.length > 0 && (
              <div className="max-h-32 overflow-auto rounded-lg bg-black/30 p-3">
                {verboseMessages.map((msg, i) => (
                  <p key={i} className="text-xs text-white/50">
                    {msg}
                  </p>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        )}
      </main>
      <ConceptStudio
        open={studioOpen}
        onClose={() => setStudioOpen(false)}
        designDisabledReason={designDisabledReason}
        onSubmit={handleConceptStudioSubmit}
      />
    </div>
  );
}

/**
 * Consumer tier's describe step — a TV pane (idle Foundry branding, matches
 * the interview screen's own placeholder copy) beside a phone pane (a single
 * textarea). No placement selector, no kickoff-mode toggle: "the point of
 * consumer is one path" (Sean, crucible#2007 D-77). Submitting always goes
 * to the adaptive interview (`handleCreate('quick')` at the call site) —
 * there is no UI here that could pick 'advanced' instead.
 */
function ConsumerDescribeStage(props: {
  concept: string;
  onConceptChange: (value: string) => void;
  onSubmit: () => void;
  promptRef: React.RefObject<HTMLTextAreaElement | null>;
  speechSupported: boolean;
  promptListening: boolean;
  onVoiceClick: () => void;
  submitDisabled: boolean;
  /** True when local isn't ready to create yet and there's no placement
   *  selector on this screen to surface why (prosumer's ProjectPlacementSelector
   *  shows this reason inline; consumer needs its own small advisory). */
  placementBlocked: boolean;
  localReadinessReason: string;
  onOpenLocalSetup: () => void;
  /** Opens the Concept Studio ("Help me shape an idea"). crucible#3313 —
   *  reachable for the consumer tier WITHOUT `flags.foundryPrototypes`:
   *  that flag exists to gate the unrelated cloud-VM prototype packages
   *  (`@volley/crucible-prototype-helper` / `-proxy`), and tying the studio
   *  to it left the studio invisible in production. The studio is a
   *  prompt-authoring aid feeding the SAME `handleCreate` path — not a
   *  path choice, so the consumer surface stays single-lane. */
  onOpenStudio: () => void;
}): React.JSX.Element {
  const eyebrowStyle = { color: 'var(--color-crucible-accent-hover)' } as const;
  return (
    <div className="flex h-full w-full">
      {/* TV side — maximized: fills the pane edge to edge, no card/border, left-aligned
          (matches Sean's reference: "maximizing the TV side"). */}
      <div
        data-testid="northstar-create-tv"
        className="flex flex-1 flex-col justify-center gap-2 px-16 lg:px-24"
      >
        <p className="text-[11px] font-semibold uppercase tracking-widest" style={eyebrowStyle}>
          Foundry
        </p>
        <h2
          className="text-4xl font-light"
          style={{ color: 'var(--text-1)', letterSpacing: '-0.01em' }}
        >
          What would you like to build?
        </h2>
        <p className="text-base text-white/35">Waiting for your first idea.</p>
      </div>

      <div className="w-px shrink-0" style={{ background: 'var(--color-crucible-border)' }} />

      {/* Phone side — the whole form: describe, then submit. No placement or
          kickoff-mode choice — "the consumer just has one path" (Sean). */}
      <div
        data-testid="northstar-create-phone"
        className="flex w-[380px] shrink-0 flex-col gap-4 px-8 py-12"
      >
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest" style={eyebrowStyle}>
            Describe
          </p>
          <p className="mt-1 text-base" style={{ color: 'var(--text-1)' }}>
            Say the core idea.
          </p>
        </div>

        <textarea
          ref={props.promptRef}
          value={props.concept}
          onChange={(e) => props.onConceptChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !props.submitDisabled)
              props.onSubmit();
          }}
          placeholder="Describe the game you want to build..."
          autoFocus
          className="min-h-[240px] flex-1 resize-none rounded-2xl border-0 p-4 text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
          style={{ background: 'rgba(255, 255, 255, 0.03)', color: 'var(--text-1)' }}
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            title={props.speechSupported ? 'Voice idea' : 'Type idea'}
            aria-label="Voice idea"
            aria-pressed={props.promptListening}
            data-testid="new-game-voice-button"
            onClick={props.onVoiceClick}
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors ${
              props.promptListening ? 'bg-crucible-accent text-black' : 'hover:bg-white/[0.08]'
            }`}
            style={{ color: props.promptListening ? undefined : 'var(--text-3)' }}
          >
            <Mic size={17} strokeWidth={1.75} />
          </button>

          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.submitDisabled}
            data-testid="new-game-create-button"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-crucible-accent text-sm font-medium text-black transition-colors hover:bg-crucible-accent-hover disabled:cursor-not-allowed disabled:bg-white/[0.08] disabled:text-white/30"
          >
            Start interview
          </button>
        </div>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={props.onOpenStudio}
            data-testid="new-game-concept-studio"
            className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] px-4 py-2 text-[13px] transition-colors hover:bg-white/[0.06]"
            style={{ color: 'var(--text-2)' }}
          >
            <Sparkles size={14} className="text-crucible-accent-hover" />
            Help me shape an idea
          </button>
        </div>

        {props.placementBlocked && (
          <p className="text-[11px] leading-relaxed text-amber-300/80">
            {props.localReadinessReason}{' '}
            <button
              type="button"
              onClick={props.onOpenLocalSetup}
              className="underline hover:text-amber-200"
            >
              Open setup
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
